import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { MatIconLoaderService } from "@app/shared/services/mat-icon-loader.service";
import { PermissionsHelperService } from "@app/shared/services/permissions-helper.service";

/**
 * @description Holds the icon, label, count and color of a status item displayed in the list area of the widget
 * @interface StatusItem
 */
export interface StatusItem<T = string> {
    icon: string;
    label: T;
    itemCount: number;
    color?: string;
}

/**
 * @description An object to hold the total status counts and each status item of a widget
 * @interface StatusItemObject
 */
export interface StatusItemObject {
    totalCount: number;
    statusItems: StatusItem[];
}

@Component({
    selector: "app-dashboard-widget",
    templateUrl: "./dashboard-widget.component.html",
    styleUrls: ["./dashboard-widget.component.css"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardWidgetComponent implements OnChanges {
    @Input() statusItems: StatusItemObject;
    @Input() actionItem: string;
    @Input() title: string;
    @Input() backgroundColor: string;

    public totalCount: number | string = "--";

    @Output() titleClickHandler: EventEmitter<any> = new EventEmitter();
    @Output() addBtnClickHandler: EventEmitter<any> = new EventEmitter();

    constructor(matIconLoaderService: MatIconLoaderService, public readonly permissionsHelperService: PermissionsHelperService) {
        this.permissionsHelperService.checkMultiplePermissions(["WidgetAction"], ["view", "edit"]);
        matIconLoaderService.importIcons([
            [`cc-clipboard`, `clipboard`],
            [`cc-folder`, `folder`],
            [`cc-user`, `user`],
        ]);
    }

    ngOnChanges(changes: NgChanges<DashboardWidgetComponent>) {
        if (changes.statusItems?.currentValue) {
            const statusItems = {
                ...changes.statusItems.currentValue,
                statusItems: [...changes.statusItems.currentValue.statusItems],
            };

            this.totalCount = statusItems.totalCount;
            // This may come back
            // statusItems.statusItems?.sort((a, b) => +b.itemCount - +a.itemCount);

            if (statusItems.statusItems?.length > 4) {
                statusItems.statusItems.splice(4, statusItems.statusItems.length - 4);
            }
            this.statusItems = statusItems;
        }

        if (changes.backgroundColor) {
            this.backgroundColor = changes.backgroundColor.currentValue;
        }
    }

    /**
     * @description Emits an event to go to the route in the parent when the title block is pressed
     */
    onTitleClick() {
        this.titleClickHandler.emit(true);
    }

    /**
     * @description Emits an event to go to the add page defined in the parent when the add button is pressed
     */
    onActionItemClick() {
        this.addBtnClickHandler.emit(true);
    }
}
